import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../../auth/AuthProvider';
import {
  useOrg,
  useUpdateOrg,
  useInvites,
  useCreateInvite,
  useRevokeInvite,
} from '../../../api/hooks/admin.hook';
import { usePlanGate } from '../../../hooks/usePlanGate';
import { cx } from 'class-variance-authority';

const planBadgeColor: Record<string, string> = {
  free: 'bg-neutral-100 text-neutral-600',
  pro: 'bg-blue-100 text-blue-700',
  elite: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700',
};

const billingStatusColor: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  trial: 'bg-blue-100 text-blue-700',
  past_due: 'bg-red-100 text-red-700',
  suspended: 'bg-yellow-100 text-yellow-700',
  cancelled: 'bg-neutral-100 text-neutral-600',
};

export const OrganizationSection = () => {
  const { user } = useAuth();
  const { data: org, isLoading } = useOrg();
  const updateOrg = useUpdateOrg();
  const { hasFeature, planInfo, isHardLimit } = usePlanGate();
  const { data: invitesData } = useInvites();
  const createInvite = useCreateInvite();
  const revokeInvite = useRevokeInvite();

  const orgRole = (user as Record<string, unknown> | null)?.org_role;
  const isOrgAdmin = orgRole === 'admin' && hasFeature('org_admin_controls');
  const canInviteMembers = orgRole === 'admin' && hasFeature('invite_members');

  const [isEditing, setIsEditing] = useState(false);
  const [orgName, setOrgName] = useState(org?.name || '');
  const [orgSlug, setOrgSlug] = useState(org?.slug || '');

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [showInviteForm, setShowInviteForm] = useState(false);

  const invites = invitesData?.invites ?? [];
  const memberCount = org?.stats?.member_count ?? 0;
  const atLimit =
    planInfo.max_members !== null && memberCount >= planInfo.max_members;

  useEffect(() => {
    if (org) {
      setOrgName(org.name);
      setOrgSlug(org.slug);
    }
  }, [org]);

  const handleSave = async () => {
    if (!isOrgAdmin) {
      toast.error('You do not have permission to update organization settings.');
      return;
    }

    try {
      await updateOrg.mutateAsync({
        name: orgName.trim(),
      });
      setIsEditing(false);
      toast.success('Organization updated successfully.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update organization.';
      toast.error(message);
    }
  };

  const handleCancel = () => {
    if (org) {
      setOrgName(org.name);
      setOrgSlug(org.slug);
    }
    setIsEditing(false);
  };

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);

    if (!inviteEmail.trim() || !inviteEmail.includes('@')) {
      setInviteError('Please enter a valid email address.');
      return;
    }

    try {
      await createInvite.mutateAsync({
        email: inviteEmail.trim().toLowerCase(),
        name: inviteName.trim() || undefined,
        role: inviteRole,
      });
      setInviteEmail('');
      setInviteName('');
      setInviteError(null);
      setShowInviteForm(false);
      toast.success('Invitation sent successfully.');
    } catch (err: unknown) {
      const apiErr = err as { error?: string; detail?: { error?: string } };
      const msg = apiErr?.detail?.error ?? apiErr?.error ?? 'Failed to send invitation.';
      if (msg === 'user_already_member') {
        setInviteError('This user is already a member.');
      } else if (msg === 'member_limit_reached') {
        setInviteError('Member limit reached. Upgrade your plan to invite more members.');
      } else {
        setInviteError(msg);
      }
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    try {
      await revokeInvite.mutateAsync(inviteId);
      toast.success('Invitation revoked.');
    } catch (err) {
      toast.error('Failed to revoke invitation.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32 text-neutral-400 text-sm">
        Loading organization...
      </div>
    );
  }

  if (!org) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
            <p className="text-sm text-neutral-600">
              Organization information is not available. You may need to be an organization admin to
              view this information.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-neutral-900">Organization</h1>
          <p className="text-sm text-neutral-600">
            View and manage your organization profile and settings.
          </p>
        </div>

        {/* Organization Profile Card */}
        <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 space-y-6">
          <header className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">Organization Profile</h2>
              <p className="text-sm text-neutral-600 mt-1">
                Basic information about your organization.
              </p>
            </div>
            {isOrgAdmin && !isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-neutral-500"
              >
                Edit
              </button>
            )}
          </header>

          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Organization Name
                </label>
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter organization name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Organization Slug
                </label>
                <input
                  type="text"
                  value={orgSlug}
                  onChange={(e) => setOrgSlug(e.target.value)}
                  disabled
                  className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm bg-neutral-50 text-neutral-500 cursor-not-allowed"
                  placeholder="Organization slug (read-only)"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  Organization slug cannot be changed after creation.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  disabled={updateOrg.isLoading}
                  className="inline-flex items-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateOrg.isLoading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={updateOrg.isLoading}
                  className="inline-flex items-center rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Organization Name
                </label>
                <p className="text-sm font-medium text-neutral-900">{org.name}</p>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Organization Slug
                </label>
                <p className="text-sm font-mono text-neutral-600 break-all">{org.slug}</p>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Plan
                </label>
                <div>
                  <span
                    className={cx(
                      'inline-block px-2 py-1 rounded text-xs font-medium uppercase tracking-wide',
                      planBadgeColor[org.plan] || planBadgeColor.free,
                    )}
                  >
                    {org.plan}
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Billing Status
                </label>
                <div>
                  <span
                    className={cx(
                      'inline-block px-2 py-1 rounded text-xs font-medium uppercase tracking-wide',
                      billingStatusColor[org.billing_status] || billingStatusColor.active,
                    )}
                  >
                    {org.billing_status}
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Created At
                </label>
                <p className="text-sm text-neutral-600">
                  {new Date(org.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}
        </section>

        {/* Organization Stats Card */}
        {org.stats && (
          <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 space-y-6">
            <header>
              <h2 className="text-lg font-semibold text-neutral-900">Organization Statistics</h2>
              <p className="text-sm text-neutral-600 mt-1">
                Current usage and resource counts for your organization.
              </p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Members
                </label>
                <p className="text-2xl font-semibold text-neutral-900">
                  {org.stats.member_count.toLocaleString()}
                </p>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Projects
                </label>
                <p className="text-2xl font-semibold text-neutral-900">
                  {org.stats.project_count.toLocaleString()}
                </p>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Tokens This Month
                </label>
                <p className="text-2xl font-semibold text-neutral-900">
                  {org.stats.tokens_this_month.toLocaleString()}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Invite Members Section */}
        {canInviteMembers && (
          <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 space-y-6">
            <header className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">Invite Members</h2>
                <p className="text-sm text-neutral-600 mt-1">
                  Invite new members to join your organization by email.
                </p>
              </div>
              {!showInviteForm && (
                <button
                  onClick={() => setShowInviteForm(true)}
                  disabled={atLimit}
                  className="inline-flex items-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Invite Member
                </button>
              )}
            </header>

            {atLimit && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <p className="text-sm text-yellow-800">
                  Member limit reached ({memberCount}/{planInfo.max_members}). Upgrade your plan to invite more members.
                </p>
              </div>
            )}

            {showInviteForm && (
              <form onSubmit={handleInviteSubmit} className="space-y-4 border-t border-neutral-200 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="user@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Name (optional)
                    </label>
                    <input
                      type="text"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Role
                  </label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>
                {inviteError && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{inviteError}</p>
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={createInvite.isLoading || atLimit}
                    className="inline-flex items-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createInvite.isLoading ? 'Sending...' : 'Send Invitation'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteForm(false);
                      setInviteEmail('');
                      setInviteName('');
                      setInviteError(null);
                    }}
                    className="inline-flex items-center rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-neutral-500"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {invites.length > 0 && (
              <div className="border-t border-neutral-200 pt-6">
                <h3 className="text-sm font-semibold text-neutral-900 mb-4">Pending Invitations</h3>
                <div className="space-y-2">
                  {invites.map((invite) => (
                    <div
                      key={invite.id}
                      className="flex items-center justify-between p-3 bg-neutral-50 rounded-md"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-neutral-900">{invite.email}</p>
                        <p className="text-xs text-neutral-500">
                          Role: {invite.role} • Expires:{' '}
                          {invite.expires_at
                            ? new Date(invite.expires_at).toLocaleDateString()
                            : 'Never'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRevokeInvite(invite.id)}
                        disabled={revokeInvite.isLoading}
                        className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        Revoke
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Admin Link Card */}
        {isOrgAdmin && (
          <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">Organization Management</h2>
                <p className="text-sm text-neutral-600 mt-1">
                  Access full organization administration features including member management,
                  billing, and integrations.
                </p>
              </div>
              <Link
                to="/admin"
                className="inline-flex items-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-500"
              >
                Go to Admin
              </Link>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
